import React, { useState, useEffect } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from '@/components/ui/button';
import { Camera, ChefHat } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Gift, CalendarDays } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { formatPhoneNumber } from '@/lib/utils';

// Define types for visits and rewards
interface Visit {
  id: string;
  date: string;
}

interface Reward {
  id: string;
  name: string;
  description: string;
  type: string;
  visitsRequired: number;
  active: boolean;
  created: string;
}

const CustomerScan: React.FC = () => {
  const [isScanning, setIsScanning] = useState(false);
  const [scanner, setScanner] = useState<Html5Qrcode | null>(null);
  const [scannedCustomerId, setScannedCustomerId] = useState<string | null>(null);
  const [customerData, setCustomerData] = useState<any>(null);
  const [customerVisits, setCustomerVisits] = useState<Visit[]>([]);
  const [availableRewards, setAvailableRewards] = useState<Reward[]>([]);
  const [activeRewards, setActiveRewards] = useState<Reward[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasRedeemedRecently, setHasRedeemedRecently] = useState(false);
  const navigate = useNavigate();

  // Cleanup scanner on unmount
  useEffect(() => {
    return () => {
      stopScanning();
    };
  }, []);

  // Fetch active rewards
  useEffect(() => {
    const fetchActiveRewards = async () => {
      try {
        const { data, error } = await supabase
          .from('rewards')
          .select('*')
          .eq('active', true)
          .order('visits_required', { ascending: true });
        
        if (error) {
          console.error('Error fetching active rewards:', error);
          return;
        }
        
        if (data && data.length > 0) {
          setActiveRewards(data.map(reward => ({
            id: reward.id,
            name: reward.name,
            description: reward.description,
            type: reward.type,
            visitsRequired: reward.visits_required,
            active: reward.active,
            created: reward.created_at
          })));
        }
      } catch (error) {
        console.error('Error fetching rewards:', error);
      }
    };
    
    fetchActiveRewards();
  }, []);

  // Load customer data when ID changes
  useEffect(() => {
    const loadCustomerData = async () => {
      if (!scannedCustomerId) return;
      
      setIsLoading(true);
      try {
        // Fetch customer data
        const { data: customer, error: customerError } = await supabase
          .from('customers')
          .select('*')
          .eq('id', scannedCustomerId)
          .single();
        
        if (customerError) {
          console.error('Error fetching customer data:', customerError);
          return;
        }
        
        setCustomerData(customer);
        
        // Fetch customer visits
        const { data: visits, error: visitsError } = await supabase
          .from('visits')
          .select('*')
          .eq('customer_id', scannedCustomerId)
          .order('date', { ascending: false });
        
        if (visitsError) {
          console.error('Error fetching visits:', visitsError);
          return;
        }
        
        console.log('Fetched visits from database:', visits);
        const visitsArray = visits || [];
        setCustomerVisits(visitsArray);
        
        // Fetch redeemed rewards to determine if they've redeemed recently
        const { data: redeemedRewards, error: redeemedError } = await supabase
          .from('redeemed_rewards')
          .select('*')
          .eq('customer_id', scannedCustomerId);
        
        if (redeemedError) {
          console.error('Error fetching redeemed rewards:', redeemedError);
          return;
        }
        
        // Calculate rewards directly using fresh database data
        const calculatedRewards = calculateCustomerRewards(visitsArray, redeemedRewards || []);
        console.log('Calculated rewards using fresh data:', calculatedRewards);
        setAvailableRewards(calculatedRewards);
        
        // Check if they've redeemed recently (within the last 30 days)
        const recentRedemptions = (redeemedRewards || []).filter(reward => {
          const redeemDate = new Date(reward.date);
          const now = new Date();
          const daysDiff = (now.getTime() - redeemDate.getTime()) / (1000 * 60 * 60 * 24);
          return daysDiff <= 30; // Within the last 30 days
        }).length;
        
        // If they've redeemed rewards recently but don't have available rewards now,
        // they've used their rewards already
        setHasRedeemedRecently(recentRedemptions > 0 && calculatedRewards.length === 0);
        
      } catch (error) {
        console.error('Error loading customer data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadCustomerData();
  }, [scannedCustomerId]);

  // Auto-reset timer after successful scan
  useEffect(() => {
    let resetTimer: NodeJS.Timeout | null = null;
    
    if (scannedCustomerId) {
      // Set a timer to reset the page after 1 minute
      resetTimer = setTimeout(() => {
        setScannedCustomerId(null);
        setCustomerData(null);
        setCustomerVisits([]);
        setAvailableRewards([]);
      }, 60000); // 60 seconds = 1 minute
    }
    
    // Cleanup timer on component unmount or when scannedCustomerId changes
    return () => {
      if (resetTimer) {
        clearTimeout(resetTimer);
      }
    };
  }, [scannedCustomerId]);

  // Get the lowest number of visits required for any active reward
  const getMinVisitsRequired = (): number => {
    if (activeRewards.length === 0) return 2; // Default fallback
    return Math.min(...activeRewards.map(reward => reward.visitsRequired));
  };

  // Get the entrée reward (or first reward if no entrée is found)
  const getEntreeReward = (): Reward | null => {
    const entrée = activeRewards.find(r => 
      r.name.toLowerCase().includes('entrée') || 
      r.name.toLowerCase().includes('entree')
    );
    return entrée || (activeRewards.length > 0 ? activeRewards[0] : null);
  };

  // Get appropriate reward message for customers with no available rewards
  const getRewardInfoMessage = (): string => {
    const entrée = getEntreeReward();
    if (entrée) {
      return `Visit ${entrée.visitsRequired} times in the same week to earn ${entrée.name.toLowerCase()}!`;
    } else if (activeRewards.length > 0) {
      const reward = activeRewards[0];
      return `Visit ${reward.visitsRequired} times in the same week to earn a reward!`;
    } else {
      return "Keep visiting to earn rewards!";
    }
  };

  // Get a more detailed reward message for the info box
  const getDetailedRewardMessage = (): string => {
    const entrée = getEntreeReward();
    if (entrée) {
      return `You have ${customerVisits.length} total visits. Remember, you need at least ${entrée.visitsRequired} visits in the <strong>same week</strong> to earn ${entrée.name.toLowerCase()}!`;
    } else if (activeRewards.length > 0) {
      const reward = activeRewards[0];
      return `You have ${customerVisits.length} total visits. Remember, you need at least ${reward.visitsRequired} visits in the <strong>same week</strong> to earn a reward!`;
    } else {
      return `You have ${customerVisits.length} total visits. We'll notify you when new rewards are available!`;
    }
  };

  const startScanning = async () => {
    console.log('Starting scanning on customer scan page - modal should NOT appear');
    setIsScanning(true);
    setScannedCustomerId(null);
    setCustomerData(null);
    setCustomerVisits([]);

    setTimeout(async () => {
      try {
        // List available cameras first
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        
        if (videoDevices.length === 0) {
          toast.error('No cameras found on your device');
          setIsScanning(false);
          return;
        }

        // Try to create scanner
        const newScanner = new Html5Qrcode('qr-reader');
        setScanner(newScanner);

        // Configure camera options
        const config = {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1,
        };

        let hasScanned = false;

        // Try to start with the first available camera
        await newScanner.start(
          { facingMode: 'environment' }, // Use back camera by default
          config,
          async (decodedText) => {
            // Prevent multiple scans
            if (hasScanned) return;

            try {
              console.log('Scanned QR code:', decodedText);
              
              // Validate QR code format
              if (!decodedText.match(/^dinetrack:[a-zA-Z0-9-_]+$/)) {
                toast.error('Invalid QR code. Please ask staff for assistance.');
                return;
              }

              hasScanned = true;
              const customerId = decodedText.replace('dinetrack:', '');
              
              try {
                // Show customer profile information immediately
                setScannedCustomerId(customerId);
                stopScanning();
                
                // Save notification to database for staff
                console.log('Sending notification to staff for customer:', customerId);
                
                try {
                  // Get current time in ISO format
                  const now = new Date().toISOString();
                  console.log(`Creating notification with timestamp: ${now}`);
                  
                  // Try RPC function first as it's more likely to work with RLS
                  try {
                    await supabase.rpc('insert_staff_notification', {
                      p_type: 'scan',
                      p_message: `Customer has scanned their QR code`,
                      p_customer_id: customerId
                    });
                    console.log('Notification saved via RPC function');
                    toast.success('Check-in successful!');
                  } catch (rpcError) {
                    console.error('RPC notification failed:', rpcError);
                    
                    // Fallback to direct insert
                    const { error } = await supabase
                      .from('staff_notifications')
                      .insert({
                        type: 'scan',
                        message: `Customer has scanned their QR code`,
                        customer_id: customerId,
                        created_at: now
                      });

                    if (error) {
                      console.error('Error saving notification to database:', error);
                      // Still show success to customer even if notification fails
                      toast.success('Check-in successful!');
                    } else {
                      console.log('Notification saved to database successfully');
                      toast.success('Check-in successful!');
                    }
                  }
                } catch (notificationError) {
                  console.error('Failed to save staff notification:', notificationError);
                  // Still show success to customer even if notification fails
                  toast.success('Check-in successful!');
                }
              } catch (error) {
                console.error('Error during QR processing:', error);
                toast.error('Error processing QR code. Please try again.');
                stopScanning();
              }
              
            } catch (err) {
              console.error('Error processing QR code:', err);
              toast.error('Error processing QR code. Please try again.');
            }
          },
          (errorMessage) => {
            // Only log critical errors, ignore normal scanning errors
            if (!errorMessage.includes('No QR code found')) {
              console.debug('QR scan error:', errorMessage);
            }
          }
        );
      } catch (err) {
        console.error('Camera start error:', err);
        let errorMessage = 'Failed to access camera. Please allow camera access and try again.';
        if (err instanceof Error) {
          errorMessage = err.message;
        }
        toast.error(errorMessage);
        setIsScanning(false);
        if (scanner) {
          scanner.clear();
          setScanner(null);
        }
      }
    }, 100);
  };

  const stopScanning = async () => {
    if (scanner) {
      try {
        await scanner.stop();
        await scanner.clear();
      } catch (err) {
        console.error('Error during scanner cleanup:', err);
      } finally {
        setScanner(null);
      }
    }
    setIsScanning(false);
  };

  // Reset scan and try again
  const handleScanAgain = () => {
    setScannedCustomerId(null);
    setCustomerData(null);
    setCustomerVisits([]);
    startScanning();
  };

  // Helper function to check if a date is within the past 30 days
  const isWithinPast30Days = (date: string): boolean => {
    const visitDate = new Date(date);
    const now = new Date();
    const daysDiff = (now.getTime() - visitDate.getTime()) / (1000 * 60 * 60 * 24);
    return daysDiff <= 30;
  };

  // Calculate customer rewards using fresh database data (same logic as AppContext)
  const calculateCustomerRewards = (visits: any[], redeemedRewards: any[]): Reward[] => {
    // Get visits within the past 30 days
    const recentVisits = visits.filter(visit => isWithinPast30Days(visit.date));

    // Get all visits grouped by week
    const visitsByWeek: { [weekKey: string]: any[] } = {};
    recentVisits.forEach(visit => {
      const visitDate = new Date(visit.date);
      // Use Monday-based week key (same logic as AppContext)
      const day = visitDate.getDay();
      const diff = visitDate.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday (0)
      const monday = new Date(visitDate);
      monday.setDate(diff);
      const weekKey = `${monday.getFullYear()}-${monday.getMonth() + 1}-${monday.getDate()}`;
      
      if (!visitsByWeek[weekKey]) {
        visitsByWeek[weekKey] = [];
      }
      visitsByWeek[weekKey].push(visit);
    });

    console.log('Visits by week:', visitsByWeek);

    // Find active rewards and calculate stacked rewards
    const stackedRewards: Reward[] = [];
    
    activeRewards.forEach(reward => {
      // Get redeemed rewards for this specific reward type in the past 30 days
      const redeemedRecently = redeemedRewards.filter(r => 
        r.reward_id === reward.id && isWithinPast30Days(r.date)
      ).length;

      // Calculate total earned rewards across all weeks in the past 30 days
      let totalEarnedRewards = 0;
      
      // For each week, check if they qualify for the reward
      Object.values(visitsByWeek).forEach(weekVisits => {
        if (weekVisits.length >= reward.visitsRequired) {
          // Calculate how many rewards they earned this week
          const weeklyRewards = Math.floor(weekVisits.length / reward.visitsRequired);
          totalEarnedRewards += weeklyRewards;
        }
      });

      console.log(`Reward ${reward.name}: totalEarned=${totalEarnedRewards}, redeemedRecently=${redeemedRecently}`);

      // Calculate how many unclaimed rewards they have
      const unclaimedRewardsCount = Math.max(0, totalEarnedRewards - redeemedRecently);
      
      // Add duplicate rewards for each unclaimed reward
      for (let i = 0; i < unclaimedRewardsCount; i++) {
        stackedRewards.push(reward);
      }
    });

    return stackedRewards;
  };

  return (
    <div className="min-h-screen flex flex-col items-center bg-white">
      {/* Header */}
      <header className="w-full bg-black text-white py-3 px-4 shadow-lg">
        <div className="container mx-auto flex items-center justify-center">
          <img 
            src="/thai-express-logo.png" 
            alt="Thai Express" 
            className="h-8 mr-3 object-contain"
          />
          <h1 className="text-xl font-bold">Loyalty Program</h1>
        </div>
      </header>

      <div className="w-full max-w-md mx-auto text-center space-y-8 pt-6 px-4">
        {!isScanning && !scannedCustomerId ? (
          <>
            {/* Hero Section */}
            <div className="mb-8">
              <div className="flex justify-center mb-6">
                <div className="bg-red-600 p-4 rounded-full shadow-lg">
                  <Gift className="h-12 w-12 text-white" />
                </div>
              </div>
              <h2 className="text-3xl font-bold text-black mb-4">
                Welcome!
              </h2>
              <p className="text-gray-600 text-lg mb-6">
                Join our loyalty program and earn rewards
              </p>
              <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                <p className="text-red-700 font-semibold">
                  Visit 3 times in one week = Free Entrée! 🍜
                </p>
              </div>
            </div>

            {/* Call to Action */}
            <div className="space-y-6">
              <Button
                size="lg"
                className="w-full py-6 text-xl bg-red-600 hover:bg-red-700 text-white shadow-lg"
                onClick={startScanning}
              >
                <Camera className="h-8 w-8 mr-4" />
                Scan Your QR Code
              </Button>
              
              <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
                <p className="text-sm text-gray-700 mb-2">
                  <strong>Need a QR code?</strong>
                </p>
                <p className="text-sm text-gray-600">
                  Ask any staff member - it's free and instant!
                </p>
              </div>
              
              <p className="text-xs text-gray-500">
                Camera access required when prompted
              </p>
            </div>
          </>
        ) : isScanning ? (
          <div className="space-y-4">
            <div 
              id="qr-reader"
              className="overflow-hidden rounded-lg bg-white shadow-lg qr-scanner-container"
              style={{ aspectRatio: '1', width: '100%', maxWidth: '500px', margin: '0 auto' }}
            />
            <p className="text-sm text-muted-foreground">
              Hold your QR code in front of the camera
            </p>
            <Button
              variant="outline"
              size="lg"
              className="w-full border-restaurant-primary text-restaurant-primary hover:bg-restaurant-primary hover:text-white"
              onClick={stopScanning}
            >
              Cancel Scanning
            </Button>
          </div>
        ) : isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-restaurant-primary mb-4"></div>
            <p className="text-restaurant-secondary">Loading your profile...</p>
          </div>
        ) : customerData ? (
          <div className="space-y-6 animate-fade-in">
            <Card className="border-t-4 border-t-restaurant-primary shadow-md">
              <CardHeader className="bg-gray-50 pb-2">
                <CardTitle className="flex items-center gap-2 justify-center text-restaurant-secondary">
                  <CalendarDays className="h-5 w-5 text-restaurant-primary" />
                  Visit History
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <p className="text-lg font-medium flex items-center justify-center">
                  <span className="bg-restaurant-primary/10 text-restaurant-primary px-3 py-1 rounded-full">
                    {customerVisits.length} {customerVisits.length === 1 ? 'Visit' : 'Visits'}
                  </span>
                </p>
                {customerVisits.length > 0 && (
                  <p className="text-sm text-muted-foreground mt-2 text-center">
                    Last visit: {new Date(customerVisits[0].date).toLocaleDateString()}
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="border-t-4 border-t-restaurant-primary shadow-md">
              <CardHeader className="bg-gray-50 pb-2">
                <CardTitle className="flex items-center gap-2 justify-center text-restaurant-secondary">
                  <Gift className="h-5 w-5 text-restaurant-primary" />
                  Available Rewards
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                {availableRewards.length === 0 ? (
                  <div>
                    <p className="text-center text-muted-foreground py-4">
                      {hasRedeemedRecently 
                        ? "You've already redeemed your rewards recently!" 
                        : customerVisits.length > 0
                          ? getRewardInfoMessage()
                          : "No rewards available yet. Keep visiting to earn rewards!"}
                    </p>
                    {customerVisits.length >= getMinVisitsRequired() && !hasRedeemedRecently && (
                      <div className="mt-2 p-2 bg-yellow-50 rounded-md border border-yellow-200">
                        <p className="text-xs text-yellow-700 text-center" dangerouslySetInnerHTML={{ __html: getDetailedRewardMessage() }}></p>
                      </div>
                    )}
                    {hasRedeemedRecently && (
                      <div className="mt-2 p-2 bg-green-50 rounded-md border border-green-200">
                        <p className="text-xs text-green-700 text-center">
                          You've used your reward(s). Visit more to earn additional rewards!
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Group rewards by ID and count how many of each type */}
                    {(() => {
                      const groupedRewards = availableRewards.reduce((acc, reward) => {
                        if (!acc[reward.id]) {
                          acc[reward.id] = {
                            ...reward,
                            count: 0
                          };
                        }
                        acc[reward.id].count++;
                        return acc;
                      }, {} as Record<string, Reward & { count: number }>);
                      
                      return Object.values(groupedRewards).map((reward, index) => (
                        <div 
                          key={`${reward.id}-${index}`}
                          className="p-4 rounded-lg bg-restaurant-primary/5 border border-restaurant-primary/20"
                        >
                          <div className="flex flex-col items-center text-center">
                            <Gift className="h-6 w-6 text-restaurant-primary mb-2" />
                            <h3 className="font-semibold text-lg text-restaurant-primary flex items-center">
                              {reward.name}
                              {reward.count > 1 && (
                                <span className="ml-2 bg-restaurant-primary text-white text-xs px-2 py-0.5 rounded-full">
                                  x{reward.count}
                                </span>
                              )}
                            </h3>
                            <p className="text-muted-foreground mt-1">
                              {reward.description}
                            </p>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                )}
              </CardContent>
            </Card>

            <Button
              variant="outline"
              size="lg"
              className="w-full border-restaurant-primary text-restaurant-primary hover:bg-restaurant-primary hover:text-white"
              onClick={handleScanAgain}
            >
              Scan Another Code
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-restaurant-secondary">No customer data found. Please try scanning again.</p>
            <Button
              variant="outline"
              size="lg"
              className="mt-4 border-restaurant-primary text-restaurant-primary hover:bg-restaurant-primary hover:text-white"
              onClick={handleScanAgain}
            >
              Scan Again
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerScan; 